import { BookOpen, FileText, History } from 'lucide-react';

import { ProblemDescription } from './ProblemDescription';
import { SqlSchemaPanel } from './SqlSchemaPanel';
import { SubmissionsTab } from './SubmissionsTab';
import { EditorialTab } from './EditorialTab';
import { PaneMaximizeButton } from './PaneControls';

import type { Problem } from '@/lib/api/problems';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

interface Props {
  problem: Problem;
  slug: string;
  isMaximized?: boolean;
  onToggleMaximize?: () => void;
  onOpenSubmission: (id: string, verdict: string) => void;
  /**
   * Whether the caller has earned the editorial — an accepted submission, or a
   * staff role. Decided by ProblemWorkspace, which is where the just-now solve
   * is known; see useEditorialAccess.
   */
  editorialUnlocked?: boolean;
  /**
   * Controlled tab. Lifted out of this component because the accepted-solve
   * panel offers "Read the editorial", and that button lives in the *other*
   * pane — it has to be able to drive this one. Both desktop and mobile mount
   * their own LeftPanel, so shared state also keeps the tab from resetting
   * when the layout switches under a resize.
   */
  tab: TabKey;
  onTabChange: (tab: TabKey) => void;
}

export type TabKey = 'description' | 'editorial' | 'submissions';

const triggerCls =
  'rounded-none border-b-2 border-transparent px-3 py-2 text-sm font-medium gap-1.5 ' +
  'data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none -mb-px';

export function LeftPanel({
  problem,
  slug,
  isMaximized,
  onToggleMaximize,
  onOpenSubmission,
  editorialUnlocked = false,
  tab,
  onTabChange,
}: Props) {
  // The tab is mounted only once unlocked, matching the ExplainSolveDialog
  // precedent: a locked tab a student can click is an advertisement for the
  // answer. If it disappears again (it cannot today, but a role change on
  // re-login could), fall back to the description rather than render nothing.
  const activeTab = tab === 'editorial' && !editorialUnlocked ? 'description' : tab;

  return (
    <Tabs
      value={activeTab}
      onValueChange={(v) => onTabChange(v as TabKey)}
      className="flex h-full flex-col"
    >
      {/* overflow-x-auto: at 390px three tabs plus the maximize button can
          exceed the pane, and the strip is the right thing to scroll — not the
          page. */}
      <TabsList className="h-auto w-full justify-start overflow-x-auto rounded-none border-b border-border bg-muted/30 px-3 py-0 shrink-0">
        <TabsTrigger value="description" className={triggerCls}>
          <FileText className="h-3.5 w-3.5" />
          Description
        </TabsTrigger>
        {editorialUnlocked && (
          <TabsTrigger value="editorial" className={triggerCls}>
            <BookOpen className="h-3.5 w-3.5" />
            Editorial
          </TabsTrigger>
        )}
        <TabsTrigger value="submissions" className={triggerCls}>
          <History className="h-3.5 w-3.5" />
          Submissions
        </TabsTrigger>
        {onToggleMaximize && (
          <div className="ml-auto flex items-center pr-1">
            <PaneMaximizeButton
              isMaximized={!!isMaximized}
              onToggle={onToggleMaximize}
              label="description"
            />
          </div>
        )}
      </TabsList>

      <TabsContent value="description" className="mt-0 min-h-0 flex-1 flex-col data-[state=active]:flex">
        <div className="min-h-0 flex-1 overflow-hidden">
          <ProblemDescription problem={problem} slug={slug} />
        </div>
        {problem.execution_mode === 'sql' && (
          <div className="max-h-64 shrink-0 overflow-y-auto border-t border-border">
            <SqlSchemaPanel sqlSetup={problem.sql_setup ?? ''} />
          </div>
        )}
      </TabsContent>

      {editorialUnlocked && (
        <TabsContent value="editorial" className="mt-0 min-h-0 flex-1">
          <EditorialTab slug={slug} unlocked={editorialUnlocked} />
        </TabsContent>
      )}

      <TabsContent value="submissions" className="mt-0 min-h-0 flex-1">
        <SubmissionsTab slug={slug} onOpenSubmission={onOpenSubmission} />
      </TabsContent>
    </Tabs>
  );
}
